/**
 * @file CustomerRowItem.tsx
 * @description Single row in the admin customers list: name/email, admin-
 *   notes indicator, project count, status chip, and last-login line.
 *   Split out of CustomersList.tsx during the design-system restyle to
 *   keep that file under the 300-line cap.
 * @module Components
 * @security Renders only fields the admin-scoped list endpoint already
 *   returned; no extra API calls, no raw PII beyond that response.
 */

import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CustomerRow } from '../../lib/admin-api';
import { relativeTime } from '../../lib/relative-time';

export function CustomerRowItem({ customer }: { customer: CustomerRow }): JSX.Element {
  const { t } = useTranslation();
  const primary = customer.full_name?.trim() || customer.email || t('customers.unnamed');
  const secondary = customer.full_name?.trim() ? customer.email : null;
  return (
    <li>
      <Link
        to={`/customers/${customer.id}`}
        className="flex items-center justify-between card p-4 hover:bg-slate-50/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      >
        <div className="min-w-0">
          <p className="font-medium text-slate-900 truncate">
            {primary}
            {customer.has_admin_notes ? (
              <span
                className="ms-1.5 text-amber-600 align-middle inline-flex"
                title={t('customers.has_admin_notes_tooltip')}
                aria-label={t('customers.has_admin_notes_aria')}
              >
                <NoteIcon />
              </span>
            ) : null}
          </p>
          {secondary !== null ? (
            <p className="text-xs text-slate-500 truncate">{secondary}</p>
          ) : null}
          <p className="text-xs text-slate-500">
            {t('customers.project_count', { count: customer.project_count })}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 whitespace-nowrap">
          <StatusBadge status={customer.status} />
          {customer.is_record_only ? null : <LastLoginChip at={customer.last_login_at} />}
        </div>
      </Link>
    </li>
  );
}

/** Slice 66 — small "3 days ago" / "Never" line under the status pill.
 *  Uses the pure relativeTime() bucket helper so the i18n plural key is
 *  obvious + the threshold logic is unit-tested. */
function LastLoginChip({ at }: { at: string | null }): JSX.Element {
  const { t } = useTranslation();
  if (at === null) {
    return (
      <span className="text-[10px] text-slate-400">{t('customers.last_login_short.never')}</span>
    );
  }
  const rt = relativeTime(at);
  if (rt.bucket === 'invalid') return <span className="text-[10px] text-slate-400" />;
  // 'today' + 'yesterday' have no count interpolation; the rest do.
  const key = `customers.last_login_short.${rt.bucket}`;
  return (
    <span className="text-[10px] text-slate-500" title={new Date(at).toLocaleString()}>
      {t(key, { count: rt.count })}
    </span>
  );
}

function StatusBadge({ status }: { status: CustomerRow['status'] }): JSX.Element {
  const { t } = useTranslation();
  const cls =
    status === 'active' ? 'chip bg-emerald-50 text-emerald-700' : 'chip bg-amber-50 text-amber-700';
  return <span className={cls}>{t(`customers.status.${status}`)}</span>;
}

/** Pencil-note glyph — inline SVG per design system (no emoji icons). */
function NoteIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
      <path
        d="M4 13.5V16h2.5l9.5-9.5L13.5 4 4 13.5zM11.5 6l2.5 2.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
